import { Injectable, inject } from '@angular/core';
import { ActivityBin } from '../models/twitch.model.js';
import { FirebaseService } from './firebase.service.js';
import { QuotaExceededError, QuotaKind } from './quota.service.js';
import { trimBinsForDeep } from '../utils/deep-bins.js';

export interface HighlightAIResponse {
  startTime: number;
  sentiment: string;
  description: string;
}

export interface HighlightDeepAnalysisResponse {
  startTime: number;
  endTime: number;
  description: string;
  sentiment: string;
}

export interface HighlightSearchResponse {
  startTime: number;
  endTime: number;
  description: string;
  confidence: number; // 0-100 self-reported relevance to the user's query
}

export interface RetryInfo {
  attempt: number;       // 1-based
  maxAttempts: number;
  remainingSec: number;  // countdown to next try
  reason: string;        // message from server
}

export type OnRetry = (info: RetryInfo) => void;

// Client-side retry delays (seconds). Applied AFTER server already burned its own
// short retries (1+3+9s). These give the Gemini capacity pool time to recover.
const CLIENT_RETRY_DELAYS_SEC = [15, 30, 60];

@Injectable({
  providedIn: 'root'
})
export class GeminiService {
  private readonly firebaseService = inject(FirebaseService);

  constructor() { }

  async analyzeMultipleChatHighlights(
    clusters: { startTime: number; messages: string[] }[],
    context: { streamer: string; title: string; game: string },
    onRetry?: OnRetry
  ): Promise<HighlightAIResponse[]> {

    const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
    if (!cloudRunUrl) {
      throw new Error('Cloud Run URL не настроен. Проверьте env.js');
    }

    const result = await this.callWithRetry(
      `${cloudRunUrl}/analyze-highlights`,
      { clusters, context },
      onRetry
    );

    if (!Array.isArray(result)) {
      throw new Error('Некорректный формат ответа от сервера (ожидался массив объектов).');
    }
    if (result.length === 0) {
      throw new Error('Ответ не содержит описаний (пустой массив). Возможно, следует скорректировать промпт ИИ.');
    }
    if (!result.every((item: any) => item.startTime !== undefined && item.sentiment && item.description)) {
      throw new Error('Сервер вернул ответ без необходимых полей (startTime, sentiment, description).');
    }
    return result.map((item: any) => ({
      ...item,
      startTime: Number(item.startTime)
    })) as HighlightAIResponse[];
  }

  async performDeepChatAnalysis(
    bins: ActivityBin[],
    // streamerLogin: viewers mention the streamer by login, which differs from a localized display name.
    context: { streamer: string; streamerLogin?: string; title: string; game: string },
    onRetry?: OnRetry,
    excludeRanges?: { startTime: number; endTime: number }[],
  ): Promise<HighlightDeepAnalysisResponse[]> {

    const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
    if (!cloudRunUrl) {
      throw new Error('Cloud Run URL не настроен. Проверьте env.js');
    }

    // ≤10 messages per bin (spread over the bin) + distinct authors, bots excluded — see
    // utils/deep-bins.ts. ~10MB → ~500KB on xQc-tier chats.
    const body: Record<string, unknown> = { bins: trimBinsForDeep(bins), context };
    if (excludeRanges && excludeRanges.length > 0) body.excludeRanges = excludeRanges;

    const result = await this.callWithRetry(
      `${cloudRunUrl}/analyze-deep`,
      body,
      onRetry
    );

    if (!Array.isArray(result)) {
      throw new Error('Некорректный формат ответа от сервера при глубоком анализе.');
    }
    // Empty result is a legitimate outcome on a re-run with excludeRanges (server
    // was told "find OTHER moments, returning [] is allowed"). Caller must
    // distinguish this case from a first-run empty (which is a model malfunction).
    if (result.length > 0 && !result.every((item: any) => item.startTime !== undefined && item.endTime !== undefined && item.description && item.sentiment)) {
      throw new Error('Сервер вернул ответ без необходимых полей.');
    }
    return result.map((item: any) => ({
      ...item,
      startTime: Number(item.startTime),
      endTime: Number(item.endTime)
    })) as HighlightDeepAnalysisResponse[];
  }

  async performSearchAnalysis(
    bins: ActivityBin[],
    context: { streamer: string; title: string; game: string },
    query: string,
    onRetry?: OnRetry,
  ): Promise<HighlightSearchResponse[]> {

    const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
    if (!cloudRunUrl) {
      throw new Error('Cloud Run URL не настроен. Проверьте env.js');
    }

    // Same defense-in-depth trim as performDeepChatAnalysis — server re-trims too.
    const trimmedBins = bins.map(b => ({
      startTime: b.startTime,
      messageCount: b.messageCount,
      messages: b.messages.slice(0, 10),
    }));

    const result = await this.callWithRetry(
      `${cloudRunUrl}/analyze-search`,
      { bins: trimmedBins, context, query },
      onRetry
    );

    if (!Array.isArray(result)) {
      throw new Error('Некорректный формат ответа от сервера при ИИ-поиске.');
    }
    // Empty result is a legitimate outcome — off-topic query or genuinely no match,
    // not a malfunction. Caller (runAiSearch) surfaces it as a neutral info message.
    if (result.length > 0 && !result.every((item: any) => item.startTime !== undefined && item.endTime !== undefined && item.description && item.confidence !== undefined)) {
      throw new Error('Сервер вернул ответ без необходимых полей.');
    }
    return result.map((item: any) => ({
      ...item,
      startTime: Number(item.startTime),
      endTime: Number(item.endTime),
      confidence: Number(item.confidence),
    })) as HighlightSearchResponse[];
  }

  private async callWithRetry(url: string, body: unknown, onRetry?: OnRetry): Promise<any> {
    const maxAttempts = CLIENT_RETRY_DELAYS_SEC.length + 1;
    let lastReason = '';

    const headers = await this.firebaseService.buildCloudRunHeaders();
    if (!headers) {
      throw new Error('Войдите в аккаунт, чтобы воспользоваться AI-анализом.');
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      let response: Response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } catch (networkError) {
        // Network failure (offline, DNS, CORS etc) — treat as retryable.
        lastReason = networkError instanceof Error ? networkError.message : 'Сетевая ошибка';
        if (attempt === maxAttempts) {
          throw new Error(`Сетевая ошибка после ${maxAttempts} попыток: ${lastReason}`);
        }
        await this.waitWithCountdown(CLIENT_RETRY_DELAYS_SEC[attempt - 1], attempt, maxAttempts, lastReason, onRetry);
        continue;
      }

      if (response.ok) {
        return await response.json();
      }

      // Try to parse server's structured error for retryable flag
      let payload: any = null;
      try { payload = await response.json(); } catch { /* non-JSON body */ }

      // Daily-quota 429 — typed error so AppComponent can show Pro upsell modal.
      // NOT retryable (it's deterministic until UTC midnight).
      if (response.status === 429 && payload?.error === 'QUOTA_EXCEEDED') {
        throw new QuotaExceededError(
          payload.kind as QuotaKind,
          Number(payload.limit) || 0,
          payload.resetAt || null,
        );
      }

      // Retry only when server explicitly flags retryable: true. Gemini 429 (Google
      // quota) is not retryable either — fails fast with billing hint.
      const retryable = payload?.retryable === true;
      const userMessage = payload?.error || `HTTP ${response.status}`;
      lastReason = payload?.message || userMessage;

      if (!retryable || attempt === maxAttempts) {
        throw new Error(retryable
          ? `${userMessage}. Попробуйте позже.`
          : userMessage);
      }

      await this.waitWithCountdown(CLIENT_RETRY_DELAYS_SEC[attempt - 1], attempt, maxAttempts, lastReason, onRetry);
    }

    throw new Error(`Не удалось выполнить запрос после ${maxAttempts} попыток. ${lastReason}`);
  }

  private async waitWithCountdown(
    totalSec: number,
    attempt: number,
    maxAttempts: number,
    reason: string,
    onRetry?: OnRetry
  ): Promise<void> {
    for (let remainingSec = totalSec; remainingSec > 0; remainingSec--) {
      onRetry?.({ attempt, maxAttempts, remainingSec, reason });
      await new Promise(r => setTimeout(r, 1000));
    }
  }
}
